import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Calendar, Tag } from 'lucide-react';
import Header from '../components/Header';
import SEOHead, { blogPostSchema } from '../components/SEOHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to validate logo URLs
const isValidLogoUrl = (url) => {
  if (!url) return false;
  return url.includes('cloudfront.net') || 
         url.includes('amazonaws.com') || 
         url.includes('crystalmedia') ||
         url.startsWith('/');
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [settings, setSettings] = useState(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [isPreview, setIsPreview] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Check for preview mode
        const params = new URLSearchParams(window.location.search);
        const previewId = params.get('preview');
        const previewToken = params.get('token');

        const [settingsRes, postRes] = await Promise.all([
          fetch(`${API_URL}/api/content/settings`),
          previewId && previewToken
            ? fetch(`${API_URL}/api/blog/preview/${previewId}?token=${previewToken}`)
            : fetch(`${API_URL}/api/blog/posts/${slug}`)
        ]);

        const settingsData = await settingsRes.json();
        
        if (postRes.ok) {
          const postData = await postRes.json();
          setPost(postData);
          if (previewId) setIsPreview(true);
        } else {
          navigate('/blog');
          return;
        }

        setSettings(settingsData);
        const photoLogo = settingsData.photo_logo_url || settingsData.logo_url || '';
        setLogoUrl(isValidLogoUrl(photoLogo) ? photoLogo : '');
      } catch (error) {
        console.error('Error fetching data:', error);
        navigate('/blog');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug, navigate]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Render content blocks
  const renderBlock = (block) => {
    switch (block.type) {
      case 'text':
        return (
          <div key={block.id} className={`mb-12 ${block.layout === 'centered' ? 'text-center' : ''}`}>
            {block.heading && (
              <h2 className="font-presti text-2xl md:text-3xl text-black mb-6">{block.heading}</h2>
            )}
            {block.content && (
              <div className="prose prose-lg max-w-none text-black/70 leading-relaxed whitespace-pre-line">
                {block.content}
              </div>
            )}
          </div>
        );

      case 'image': {
        const imgFp = window.innerWidth < 768
          ? (block.image_focal_mobile || { x: 50, y: 50 })
          : (block.image_focal_desktop || { x: 50, y: 50 });
        return (
          <figure key={block.id} className={`mb-12 ${block.layout === 'wide' ? '-mx-6 lg:-mx-20' : ''}`}>
            {block.image_url && (
              <img 
                src={block.image_url} 
                alt={block.image_alt || block.image_caption || ''} 
                className="w-full h-auto rounded-lg shadow-sm"
                style={{ objectPosition: `${imgFp.x}% ${imgFp.y}%` }}
              />
            )}
            {block.image_caption && (
              <figcaption className="text-sm text-black/40 mt-4 text-center italic">
                {block.image_caption}
              </figcaption>
            )}
          </figure>
        );
      }

      case 'video':
        return (
          <div key={block.id} className="mb-12">
            {block.video_url && (
              <div className="relative aspect-video overflow-hidden rounded-lg shadow-sm">
                <video 
                  src={block.video_url} 
                  controls 
                  className="w-full h-full"
                  poster={block.image_url}
                />
              </div>
            )}
          </div>
        );

      case 'gif':
        return (
          <figure key={block.id} className="mb-12">
            {block.gif_url && (
              <img 
                src={block.gif_url} 
                alt={block.image_caption || ''} 
                className="w-full max-w-2xl mx-auto rounded-lg"
              />
            )}
            {block.image_caption && (
              <figcaption className="text-sm text-black/40 mt-4 text-center italic">
                {block.image_caption}
              </figcaption>
            )}
          </figure>
        );

      case 'quote':
        return (
          <blockquote key={block.id} className="mb-12 py-8 border-l-4 border-black/20 pl-8 lg:pl-12">
            <p className="font-presti text-xl md:text-2xl text-black/80 italic leading-relaxed mb-4">
              "{block.quote_text}"
            </p>
            {block.quote_author && (
              <cite className="text-sm text-black/40 not-italic">— {block.quote_author}</cite>
            )}
          </blockquote>
        );

      case 'excerpt':
        return (
          <div key={block.id} className="mb-12 bg-[#f5f5f5] p-8 lg:p-12 rounded-lg">
            <p className="font-presti text-xl md:text-2xl text-black/80 italic leading-relaxed text-center">
              {block.excerpt_text}
            </p>
          </div>
        );

      case 'gallery':
        return (
          <div key={block.id} className="mb-12">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {block.gallery_images?.map((img, i) => (
                <figure key={i} className="overflow-hidden rounded-lg">
                  <img 
                    src={img.url || img} 
                    alt={img.caption || ''} 
                    className="w-full h-full object-cover aspect-square hover:scale-105 transition-transform duration-500"
                  />
                </figure>
              ))}
            </div>
          </div>
        );

      case 'collection_link':
        return (
          <div key={block.id} className="mb-12">
            {post?.linked_collection && (
              <Link 
                to={`/collection/${block.collection_id}`}
                className="block bg-white border border-black/10 rounded-lg overflow-hidden hover:shadow-lg transition-shadow group"
              >
                <div className="flex flex-col md:flex-row">
                  {post.linked_collection.cover_image_url && (
                    <div className="md:w-1/3 aspect-[4/3] md:aspect-auto overflow-hidden">
                      <img 
                        src={post.linked_collection.cover_image_url} 
                        alt={post.linked_collection.couple}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="p-6 md:p-8 flex flex-col justify-center">
                    <span className="text-[10px] tracking-[0.2em] uppercase text-black/30 mb-2">View Collection</span>
                    <h3 className="font-presti text-xl text-black mb-2 group-hover:opacity-70 transition-opacity">
                      {post.linked_collection.couple}
                    </h3>
                    <p className="text-black/50 text-sm">{post.linked_collection.location}</p>
                    <span className="mt-4 text-xs tracking-[0.1em] uppercase text-black/40 flex items-center gap-2">
                      {block.collection_link_text || 'View Full Gallery'}
                      <ArrowRight size={12} />
                    </span>
                  </div>
                </div>
              </Link>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fafafa] flex items-center justify-center">
        <p className="text-black/30 animate-pulse">Loading...</p>
      </div>
    );
  }

  if (!post) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#fafafa]">
      {/* SEO */}
      <SEOHead
        title={post.seo_title || post.title}
        description={post.seo_description || post.excerpt}
        keywords={post.seo_keywords || []}
        image={post.featured_image_url}
        url={`/blog/${post.slug}`}
        type="article"
        jsonLd={blogPostSchema(post)}
      />
      
      {/* Shared Header */}
      <Header logoUrl={logoUrl} variant="light" />

      {/* Preview Banner */}
      {isPreview && (
        <div className="fixed top-0 left-0 right-0 z-[70] bg-amber-500 text-black text-center py-2 text-sm font-medium" data-testid="preview-banner">
          Preview Mode — This post is not published yet
        </div>
      )}

      {/* Back Link */}
      <div className="pt-24 px-6 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <Link 
            to="/blog" 
            className="inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-black/40 hover:text-black transition-colors"
          >
            <ArrowLeft size={14} />
            <span>All Posts</span>
          </Link>
        </div>
      </div>

      {/* Article Header */}
      <article className="pt-8 pb-20">
        <header className="max-w-4xl mx-auto px-6 lg:px-12 text-center mb-12">
          {post.categories && post.categories.length > 0 && (
            <div className="flex justify-center gap-2 mb-4">
              {post.categories.map((cat, i) => (
                <span key={i} className="text-[10px] tracking-[0.2em] uppercase text-black/30">
                  {cat}
                </span>
              ))}
            </div>
          )}
          
          <h1 className="font-presti text-3xl md:text-4xl lg:text-5xl text-black mb-6 leading-tight" data-testid="blog-title">
            {post.title}
          </h1>
          
          {post.excerpt && (
            <p className="text-black/50 text-lg max-w-2xl mx-auto leading-relaxed mb-8">
              {post.excerpt}
            </p>
          )}
          
          <div className="flex items-center justify-center gap-6 text-sm text-black/40">
            <div className="flex items-center gap-2">
              <Calendar size={14} />
              <span>{formatDate(post.published_at)}</span>
            </div>
            {post.tags && post.tags.length > 0 && (
              <div className="flex items-center gap-2">
                <Tag size={14} />
                <span>{post.tags.slice(0, 3).join(', ')}</span>
              </div>
            )}
          </div>
        </header>

        {/* Featured Image */}
        {post.featured_image_url && (
          <figure className="max-w-6xl mx-auto px-6 lg:px-12 mb-16">
            <img 
              src={post.featured_image_url} 
              alt={post.featured_image_alt || post.title}
              className="w-full h-auto rounded-lg shadow-lg"
            />
          </figure>
        )}

        {/* Content Blocks */}
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          {post.content_blocks && post.content_blocks.length > 0 ? (
            post.content_blocks
              .sort((a, b) => a.order - b.order)
              .map(block => renderBlock(block))
          ) : (
            <p className="text-black/30 text-center">No content available</p>
          )}
        </div>

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="max-w-3xl mx-auto px-6 lg:px-12 mt-16 pt-8 border-t border-black/10">
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag, i) => (
                <span 
                  key={i}
                  className="px-4 py-2 bg-black/5 text-xs tracking-[0.1em] uppercase text-black/50 rounded"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Linked Collection */}
        {post.linked_collection && (
          <div className="max-w-4xl mx-auto px-6 lg:px-12 mt-16">
            <h3 className="text-center text-[10px] tracking-[0.3em] uppercase text-black/30 mb-8">Related Collection</h3>
            <Link 
              to={`/collection/${post.linked_collection_id}`}
              className="block bg-white border border-black/10 rounded-lg overflow-hidden hover:shadow-lg transition-shadow group"
            >
              <div className="flex flex-col md:flex-row">
                {post.linked_collection.cover_image_url && (
                  <div className="md:w-1/3 aspect-[4/3] md:aspect-auto overflow-hidden">
                    <img 
                      src={post.linked_collection.cover_image_url} 
                      alt={post.linked_collection.couple}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                )}
                <div className="p-6 md:p-8 flex flex-col justify-center">
                  <span className="text-[10px] tracking-[0.2em] uppercase text-black/30 mb-2">View Full Collection</span>
                  <h3 className="font-presti text-xl text-black mb-2 group-hover:opacity-70 transition-opacity">
                    {post.linked_collection.couple}
                  </h3>
                  <p className="text-black/50 text-sm">{post.linked_collection.location}</p>
                </div>
              </div>
            </Link>
          </div>
        )}
      </article>

      {/* CTA */}
      <section className="py-20 bg-white border-t border-black/5">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="font-presti text-2xl md:text-3xl text-black mb-6">
            Want us to capture your story?
          </h2>
          <Link
            to="/inquire"
            className="inline-flex items-center gap-3 px-10 py-4 bg-black text-white text-xs tracking-[0.2em] uppercase hover:bg-black/80 transition-colors duration-300"
          >
            <span>Get in Touch</span>
            <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-black/5 bg-[#fafafa]">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          {logoUrl ? (
            <img src={logoUrl} alt="Crystal Media" className="h-6 w-auto opacity-50" />
          ) : (
            <span className="font-presti text-xs tracking-[0.2em] text-black/40">CRYSTAL MEDIA</span>
          )}
          <p className="text-[10px] tracking-[0.15em] uppercase text-black/30">
            © 2025 Crystal Media. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default BlogPostPage;
